import React, { useState, useMemo } from 'react';
import { Shield, CheckCircle, AlertCircle, Search, Download, Copy, Filter, FileText } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const ClauseMatrix = () => {
  const { darkMode } = useTheme();
  
  // Filter states
  const [contractType, setContractType] = useState('');
  const [clauseLoading, setClauseLoading] = useState(false);
  const [clauseAiOutput, setClauseAiOutput] = useState('');
  const [clauseAccepted, setClauseAccepted] = useState(false);
  const [dollarValue, setDollarValue] = useState('');
  const [setAside, setSetAside] = useState('');
  const [acquisitionMethod, setAcquisitionMethod] = useState('');
  const [itRequirement, setItRequirement] = useState(false);
  const [construction, setConstruction] = useState(false);
  const [serviceContract, setServiceContract] = useState(false);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('All');
  
  // Selected clauses
  const [selectedClauses, setSelectedClauses] = useState(new Set());
  
  // Clause data
  const clauses = [
    // GENERAL (always required)
    { id: 1, number: 'FAR 52.202-1', title: 'Definitions', category: 'GENERAL', status: 'Mandatory', trigger: '', include: true },
    { id: 2, number: 'FAR 52.203-3', title: 'Gratuities', category: 'GENERAL', status: 'When Applicable', trigger: 'over $150K', include: false },
    { id: 3, number: 'FAR 52.203-5', title: 'Covenant Against Contingent Fees', category: 'GENERAL', status: 'When Applicable', trigger: 'over $150K', include: false },
    { id: 4, number: 'FAR 52.203-7', title: 'Anti-Kickback Procedures', category: 'GENERAL', status: 'When Applicable', trigger: 'over $150K', include: false },
    { id: 5, number: 'FAR 52.203-12', title: 'Limitation on Payments to Influence', category: 'GENERAL', status: 'When Applicable', trigger: 'over $150K', include: false },
    { id: 6, number: 'FAR 52.204-7', title: 'System for Award Management', category: 'GENERAL', status: 'Mandatory', trigger: '', include: true },
    { id: 7, number: 'FAR 52.204-10', title: 'Reporting Executive Compensation', category: 'GENERAL', status: 'When Applicable', trigger: 'over $30K', include: false },
    { id: 8, number: 'FAR 52.204-13', title: 'System for Award Management Maintenance', category: 'GENERAL', status: 'Mandatory', trigger: '', include: true },
    { id: 9, number: 'FAR 52.204-19', title: 'Incorporation by Reference of Representations', category: 'GENERAL', status: 'Mandatory', trigger: '', include: true },
    { id: 10, number: 'FAR 52.209-6', title: 'Protecting Government\'s Interest', category: 'GENERAL', status: 'When Applicable', trigger: 'over $35K', include: false },
    
    // SMALL BUSINESS & SET-ASIDE
    { id: 11, number: 'FAR 52.219-1', title: 'Small Business Program Representations', category: 'SMALL BUSINESS & SET-ASIDE', status: 'When Applicable', trigger: 'when SB applies', include: false },
    { id: 12, number: 'FAR 52.219-6', title: 'Notice of Total Small Business Set-Aside', category: 'SMALL BUSINESS & SET-ASIDE', status: 'When Applicable', trigger: 'SB set-asides', include: false },
    { id: 13, number: 'FAR 52.219-8', title: 'Utilization of Small Business Concerns', category: 'SMALL BUSINESS & SET-ASIDE', status: 'When Applicable', trigger: 'over $750K', include: false },
    { id: 14, number: 'FAR 52.219-9', title: 'Small Business Subcontracting Plan', category: 'SMALL BUSINESS & SET-ASIDE', status: 'When Applicable', trigger: 'large business over $750K', include: false },
    { id: 15, number: 'FAR 52.219-14', title: 'Limitations on Subcontracting', category: 'SMALL BUSINESS & SET-ASIDE', status: 'When Applicable', trigger: 'SB set-asides', include: false },
    { id: 16, number: 'FAR 52.219-27', title: 'Notice of Service-Disabled Veteran-Owned SB Set-Aside', category: 'SMALL BUSINESS & SET-ASIDE', status: 'When Applicable', trigger: 'SDVOSB', include: false },
    
    // VA/VAAR SPECIFIC
    { id: 17, number: 'VAAR 852.203-70', title: 'Commercial Advertising', category: 'VA/VAAR SPECIFIC', status: 'Mandatory', trigger: 'always VA', include: true },
    { id: 18, number: 'VAAR 852.215-70', title: 'Service-Disabled Veteran-Owned Business Requirements', category: 'VA/VAAR SPECIFIC', status: 'When Applicable', trigger: 'SDVOSB', include: false },
    { id: 19, number: 'VAAR 852.219-10', title: 'VA Notice of Total SDVOSB Set-Aside', category: 'VA/VAAR SPECIFIC', status: 'When Applicable', trigger: 'SDVOSB', include: false },
    { id: 20, number: 'VAAR 852.219-11', title: 'VA Notice of Total VOSB Set-Aside', category: 'VA/VAAR SPECIFIC', status: 'When Applicable', trigger: 'VOSB', include: false },
    { id: 21, number: 'VAAR 852.232-72', title: 'Electronic Submission of Payment Requests', category: 'VA/VAAR SPECIFIC', status: 'Mandatory', trigger: 'always VA', include: true },
    { id: 22, number: 'VAAR 852.233-70', title: 'Protest Content/Alternative Resolution', category: 'VA/VAAR SPECIFIC', status: 'Mandatory', trigger: 'always VA', include: true },
    { id: 23, number: 'VAAR 852.237-70', title: 'Contractor Responsibilities', category: 'VA/VAAR SPECIFIC', status: 'When Applicable', trigger: 'services', include: false },
    { id: 24, number: 'VAAR 852.239-70', title: 'Security Requirements for Information Technology', category: 'VA/VAAR SPECIFIC', status: 'When Applicable', trigger: 'IT requirements', include: false },
    
    // LABOR (Service Contract Act)
    { id: 25, number: 'FAR 52.222-1', title: 'Notice to the Government of Labor Disputes', category: 'LABOR', status: 'When Applicable', trigger: 'services', include: false },
    { id: 26, number: 'FAR 52.222-3', title: 'Convict Labor', category: 'LABOR', status: 'Mandatory', trigger: '', include: true },
    { id: 27, number: 'FAR 52.222-21', title: 'Prohibition of Segregated Facilities', category: 'LABOR', status: 'When Applicable', trigger: 'over $10K', include: false },
    { id: 28, number: 'FAR 52.222-26', title: 'Equal Opportunity', category: 'LABOR', status: 'When Applicable', trigger: 'over $10K', include: false },
    { id: 29, number: 'FAR 52.222-35', title: 'Equal Opportunity for Veterans', category: 'LABOR', status: 'When Applicable', trigger: 'over $150K', include: false },
    { id: 30, number: 'FAR 52.222-36', title: 'Equal Opportunity for Workers with Disabilities', category: 'LABOR', status: 'When Applicable', trigger: 'over $15K', include: false },
    { id: 31, number: 'FAR 52.222-41', title: 'Service Contract Labor Standards', category: 'LABOR', status: 'When Applicable', trigger: 'service contracts', include: false },
    { id: 32, number: 'FAR 52.222-43', title: 'Fair Labor Standards Act / Service Contract Act', category: 'LABOR', status: 'When Applicable', trigger: 'service contracts', include: false },
    { id: 33, number: 'FAR 52.222-50', title: 'Combating Trafficking in Persons', category: 'LABOR', status: 'Mandatory', trigger: '', include: true },
    
    // PAYMENTS & FINANCE
    { id: 34, number: 'FAR 52.232-1', title: 'Payments', category: 'PAYMENTS & FINANCE', status: 'When Applicable', trigger: 'FFP', include: false },
    { id: 35, number: 'FAR 52.232-8', title: 'Discounts for Prompt Payment', category: 'PAYMENTS & FINANCE', status: 'When Applicable', trigger: 'commercial', include: false },
    { id: 36, number: 'FAR 52.232-18', title: 'Availability of Funds', category: 'PAYMENTS & FINANCE', status: 'Mandatory', trigger: '', include: true },
    { id: 37, number: 'FAR 52.232-33', title: 'Payment by Electronic Funds Transfer', category: 'PAYMENTS & FINANCE', status: 'Mandatory', trigger: '', include: true },
    { id: 38, number: 'FAR 52.232-39', title: 'Unenforceability of Unauthorized Obligations', category: 'PAYMENTS & FINANCE', status: 'When Applicable', trigger: 'commercial', include: false },
    
    // TERMINATION & DISPUTES
    { id: 39, number: 'FAR 52.233-1', title: 'Disputes', category: 'TERMINATION & DISPUTES', status: 'Mandatory', trigger: '', include: true },
    { id: 40, number: 'FAR 52.233-3', title: 'Protest After Award', category: 'TERMINATION & DISPUTES', status: 'Mandatory', trigger: '', include: true },
    { id: 41, number: 'FAR 52.249-1', title: 'Termination for Convenience', category: 'TERMINATION & DISPUTES', status: 'When Applicable', trigger: 'FFP simplified', include: false },
    { id: 42, number: 'FAR 52.249-2', title: 'Termination for Convenience', category: 'TERMINATION & DISPUTES', status: 'When Applicable', trigger: 'FFP over SAT', include: false },
    
    // IT/CYBER
    { id: 43, number: 'FAR 52.239-1', title: 'Privacy or Security Safeguards', category: 'IT/CYBER', status: 'When Applicable', trigger: 'IT', include: false },
    { id: 44, number: 'VAAR 852.239-70', title: 'Security Requirements for IT Resources', category: 'IT/CYBER', status: 'When Applicable', trigger: 'VA IT', include: false },
    { id: 45, number: 'FAR 52.204-21', title: 'Basic Safeguarding of Covered Contractor Information Systems', category: 'IT/CYBER', status: 'When Applicable', trigger: 'IT', include: false }
  ];

  // Filter clauses based on parameters
  const filteredClauses = useMemo(() => {
    return clauses.filter(clause => {
      // Apply search filter
      if (searchTerm && 
          !clause.number.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !clause.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Apply contract type filter
      if (contractType && 
          !clause.number.includes('FAR') && 
          clause.category !== 'VA/VAAR SPECIFIC') {
        return false;
      }

      // Apply dollar value filter
      if (dollarValue) {
        if (dollarValue === 'Under $10K' && clause.trigger.includes('$150K')) return false;
        if (dollarValue === '$10K-$250K (SAT)' && clause.trigger.includes('$30K')) return false;
        if (dollarValue === '$250K-$2M' && clause.trigger.includes('$750K')) return false;
      }

      // Apply set-aside filter
      if (setAside) {
        if (setAside === 'None' && clause.trigger.includes('SB') || clause.trigger.includes('SDVOSB') || clause.trigger.includes('VOSB')) return false;
      }

      // Apply acquisition method filter
      if (acquisitionMethod) {
        if (acquisitionMethod.includes('FAR 15 Negotiated') && !clause.number.includes('FAR')) return false;
        if (acquisitionMethod.includes('FAR 12 Commercial') && clause.trigger.includes('commercial')) return true;
      }

      // Apply IT requirement filter
      if (itRequirement && !clause.trigger.includes('IT')) return false;

      // Apply construction filter
      if (construction && !clause.trigger.includes('construction')) return false;

      // Apply service contract filter
      if (serviceContract && !clause.trigger.includes('services') && !clause.trigger.includes('service contracts')) return false;

      return true;
    });
  }, [contractType, dollarValue, setAside, acquisitionMethod, itRequirement, construction, serviceContract, searchTerm]);

  // Filter by active tab
  const visibleClauses = useMemo(() => {
    switch (activeTab) {
      case 'Mandatory':
        return filteredClauses.filter(c => c.status === 'Mandatory');
      case 'When Applicable':
        return filteredClauses.filter(c => c.status === 'When Applicable');
      case 'Optional':
        return filteredClauses.filter(c => c.status === 'Optional');
      case 'Selected':
        return filteredClauses.filter(c => selectedClauses.has(c.id));
      default:
        return filteredClauses;
    }
  }, [filteredClauses, activeTab, selectedClauses]);

  // Toggle clause selection
  const toggleClause = (id) => {
    const clause = clauses.find(c => c.id === id);
    if (clause.status === 'Mandatory') return; // Mandatory clauses cannot be unchecked
    
    setSelectedClauses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Export clause list
  const exportClauseList = () => {
    const selected = clauses.filter(c => selectedClauses.has(c.id));
    const text = selected.map(c => `${c.number} - ${c.title}`).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clause-list.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Copy clause numbers
  const copyClauseNumbers = () => {
    const selected = clauses.filter(c => selectedClauses.has(c.id));
    const numbers = selected.map(c => c.number).join(', ');
    navigator.clipboard.writeText(numbers);
  };

  // Add to solicitation (mock)
  const addToSolicitation = () => {
    alert('Clause list added to solicitation!');
  };

  // Status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Mandatory': return 'bg-red-100 text-red-800 border-red-200';
      case 'When Applicable': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Optional': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Count by status
  const counts = {
    All: clauses.length,
    Mandatory: clauses.filter(c => c.status === 'Mandatory').length,
    'When Applicable': clauses.filter(c => c.status === 'When Applicable').length,
    Optional: clauses.filter(c => c.status === 'Optional').length,
    Selected: selectedClauses.size
  };

  return (
    <div className={`min-h-screen p-6 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} border border-gray-200`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Clause Matrix</h1>
          <p className="text-lg opacity-75">Federal Acquisition Clause Selector</p>
        </div>

        {/* Filter Bar */}
        <div className={`p-6 rounded-lg mb-6 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Contract Type</label>
              <select 
                value={contractType} 
                onChange={(e) => setContractType(e.target.value)}
                className={`w-full p-2 rounded border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'}`}
              >
                <option value="">All Types</option>
                <option value="FFP">Firm Fixed Price (FFP)</option>
                <option value="T&M">Time & Materials (T&M)</option>
                <option value="CPFF">Cost Plus Fixed Fee (CPFF)</option>
                <option value="IDIQ">Indefinite Delivery Indefinite Quantity (IDIQ)</option>
                <option value="BPA">Blanket Purchase Agreement (BPA)</option>
                <option value="Task Order">Task Order</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Dollar Value Range</label>
              <select 
                value={dollarValue} 
                onChange={(e) => setDollarValue(e.target.value)}
                className={`w-full p-2 rounded border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'}`}
              >
                <option value="">All Ranges</option>
                <option value="Under $10K">Under $10K</option>
                <option value="$10K-$250K (SAT)">$10K-$250K (SAT)</option>
                <option value="$250K-$2M">$250K-$2M</option>
                <option value="$2M-$10M">$2M-$10M</option>
                <option value="Over $10M">Over $10M</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Set-Aside</label>
              <select 
                value={setAside} 
                onChange={(e) => setSetAside(e.target.value)}
                className={`w-full p-2 rounded border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'}`}
              >
                <option value="">All Set-Asides</option>
                <option value="None">None</option>
                <option value="SDVOSB">SDVOSB</option>
                <option value="VOSB">VOSB</option>
                <option value="Small Business">Small Business</option>
                <option value="8(a)">8(a)</option>
                <option value="HUBZone">HUBZone</option>
                <option value="WOSB">WOSB</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Acquisition Method</label>
              <select 
                value={acquisitionMethod} 
                onChange={(e) => setAcquisitionMethod(e.target.value)}
                className={`w-full p-2 rounded border ${darkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'}`}
              >
                <option value="">All Methods</option>
                <option value="FAR 15 Negotiated">FAR 15 Negotiated</option>
                <option value="FAR 12 Commercial">FAR 12 Commercial</option>
                <option value="FAR 13 Simplified">FAR 13 Simplified</option>
                <option value="FAR 14 Sealed Bid">FAR 14 Sealed Bid</option>
              </select>
            </div>

            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="itRequirement" 
                checked={itRequirement}
                onChange={(e) => setItRequirement(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="itRequirement">IT Requirement</label>
            </div>

            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="construction" 
                checked={construction}
                onChange={(e) => setConstruction(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="construction">Construction</label>
            </div>

            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="serviceContract" 
                checked={serviceContract}
                onChange={(e) => setServiceContract(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="serviceContract">Service Contract</label>
            </div>

            <div className="flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              <span>Auto-filtering</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6 flex items-center">
          <Search className="w-5 h-5 mr-2 opacity-75" />
          <input
            type="text"
            placeholder="Search clauses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`flex-1 p-3 rounded border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'}`}
          />
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(counts).map(([tab, count]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab 
                  ? `${darkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white'}`
                  : `${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`
              }`}
            >
              {tab} ({count})
            </button>
          ))}
        </div>

        {/* Clause Table */}
        <div className={`rounded-lg overflow-hidden border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <table className="w-full">
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <tr>
                <th className="p-4 text-left">Clause Number</th>
                <th className="p-4 text-left">Title</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Trigger Condition</th>
                <th className="p-4 text-center">Include</th>
                <th className="p-4 text-left">Notes</th>
              </tr>
            </thead>
            <tbody>
              {visibleClauses.map((clause) => (
                <tr 
                  key={clause.id} 
                  className={`${darkMode ? 'border-gray-600 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'} border-b`}
                >
                  <td className="p-4 font-mono">{clause.number}</td>
                  <td className="p-4">{clause.title}</td>
                  <td className="p-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(clause.status)}`}>
                      {clause.status}
                    </span>
                  </td>
                  <td className="p-4">{clause.trigger}</td>
                  <td className="p-4 text-center">
                    <input
                      type="checkbox"
                      checked={selectedClauses.has(clause.id) || clause.status === 'Mandatory'}
                      onChange={() => toggleClause(clause.id)}
                      disabled={clause.status === 'Mandatory'}
                      className="w-5 h-5"
                    />
                  </td>
                  <td className="p-4">
                    {clause.category}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Bar */}
        <div className={`mt-6 p-4 rounded-lg flex flex-wrap items-center justify-between gap-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="text-sm">
            Selected: <span className="font-bold">{selectedClauses.size}</span> clauses
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={exportClauseList}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
              } text-white`}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Clause List
            </button>
            
            <button 
              onClick={copyClauseNumbers}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Clause Numbers
            </button>
            
            <button 
              onClick={addToSolicitation}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                darkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'
              } text-white`}
            >
              <FileText className="w-4 h-4 mr-2" />
              Add to Solicitation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClauseMatrix;
